import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import CompanySettingsService from '../services/CompanySettingsService';
import { useAuth } from '../contexts/AuthContext';
import { AuditService } from '../services/firebase/AuditService';
import { HopeVoiceService } from '../services/HopeVoiceService';
import { AccessibilityService } from '../services/AccessibilityService';
import { db } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';

interface HeaderProps {
  showSessionStatus?: boolean;
  sessionActive?: boolean;
  user?: any; // We'll type this properly later
  userType?: 'client' | 'provider';
  onLogout?: () => void;
  onOpenProfileSettings?: () => void;
  onOpenSettings?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  showSessionStatus = false,
  sessionActive = false,
  user: propUser,
  userType: propUserType,
  onLogout: propOnLogout,
  onOpenProfileSettings,
  onOpenSettings
}) => {
  // Use Firebase Auth if available, fallback to props
  const authContext = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const user = authContext?.user || propUser;
  // Determine user type: auth context first, then route-based detection, then prop
  const isClientRoute = location.pathname.startsWith('/client') || location.pathname === '/book-appointment';
  const userType = authContext?.isClient ? 'client' : authContext?.isProvider ? 'provider' : isClientRoute ? 'client' : propUserType;

  const [companySettings, setCompanySettings] = useState({
    companyName: 'Your Practice Name',
    companyLogo: undefined as string | undefined
  });
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [readAloudOn, setReadAloudOn] = useState(() => HopeVoiceService.getSettings().enabled);
  const [highContrast, setHighContrast] = useState(() => AccessibilityService.isHighContrast());
  const [fontSizeLabel, setFontSizeLabel] = useState(() => AccessibilityService.getFontSizeLabel());
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    try {
      // Stop any playing voice/audio immediately
      HopeVoiceService.disable(); // Stops ElevenLabs audio, clears queue, disables future speech
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      document.querySelectorAll('audio').forEach(a => { a.pause(); a.currentTime = 0; });

      if (authContext) {
        const uid = authContext.user?.uid;
        const role = authContext.isProvider ? 'provider' : 'client';
        if (uid) {
          AuditService.logAuthEvent(uid, 'logout', role as 'provider' | 'client');
        }
        await authContext.logout();
        navigate('/');
      } else if (propOnLogout) {
        propOnLogout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    const companyService = CompanySettingsService.getInstance();

    const updateCompanySettings = () => {
      const settings = companyService.getSettings();
      setCompanySettings({
        companyName: settings.companyName,
        companyLogo: settings.companyLogo
      });
    };

    updateCompanySettings();
    companyService.subscribe(updateCompanySettings);

    // Load practice name from Firestore for accurate cross-browser display
    const loadPracticeNameFromFirestore = async () => {
      try {
        const uid = authContext?.user?.uid;
        if (!uid) return;

        if (authContext?.isProvider) {
          // Provider: load own practice name + logo
          const provDoc = await getDoc(doc(db, 'providers', uid));
          if (provDoc.exists()) {
            const data = provDoc.data();
            setCompanySettings(prev => ({
              ...prev,
              ...(data.practiceName && { companyName: data.practiceName }),
              ...(data.practiceLogo && { companyLogo: data.practiceLogo }),
            }));
          }
        } else if (authContext?.isClient) {
          // Client: load assigned provider's practice name + logo
          let providerDocRef: string | null = null;

          // Try direct patient doc first, then phone OTP linked patient
          const patDoc = await getDoc(doc(db, 'patients', uid));
          if (patDoc.exists() && patDoc.data().assignedProviderId) {
            providerDocRef = patDoc.data().assignedProviderId;
          } else {
            // Phone OTP user: look up linked patientId
            const userDoc = await getDoc(doc(db, 'users', uid));
            if (userDoc.exists() && userDoc.data().patientId) {
              const linkedPatDoc = await getDoc(doc(db, 'patients', userDoc.data().patientId));
              if (linkedPatDoc.exists() && linkedPatDoc.data().assignedProviderId) {
                providerDocRef = linkedPatDoc.data().assignedProviderId;
              }
            }
          }

          if (providerDocRef) {
            const provDoc = await getDoc(doc(db, 'providers', providerDocRef));
            if (provDoc.exists()) {
              const data = provDoc.data();
              setCompanySettings(prev => ({
                ...prev,
                ...(data.practiceName && { companyName: data.practiceName }),
                ...(data.practiceLogo && { companyLogo: data.practiceLogo }),
              }));
            }
          }
        }
      } catch (error) {
        // Non-critical — fall back to localStorage value
      }
    };

    loadPracticeNameFromFirestore();

    return () => {
      companyService.unsubscribe(updateCompanySettings);
    };
  }, [authContext?.user?.uid, authContext?.isProvider, authContext?.isClient]);

  // Click outside + Escape key handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowProfileDropdown(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowProfileDropdown(false);
      }
    };

    if (showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showProfileDropdown]);

  const getDashboardLink = () => {
    if (userType === 'client') return '/client-dashboard';
    if (userType === 'provider') return '/provider-dashboard';
    return '/';
  };

  const getWelcomeMessage = () => {
    if (!user) return '';
    const displayName = user.displayName || user.name || authContext?.userProfile?.displayName || 'User';
    const firstName = displayName.split(' ')[0];
    let role = 'Provider';
    if (userType === 'client') {
      role = 'Client';
    } else if (authContext?.userProfile?.primaryRole) {
      const roleMap: Record<string, string> = {
        system_admin: 'System Admin',
        clinical_director: 'Clinical Director',
        provider: 'Provider',
        administrative_staff: 'Admin Staff',
        hr_admin: 'HR Admin',
      };
      role = roleMap[authContext.userProfile.primaryRole] || 'Provider';
    }
    return `Welcome, ${firstName} (${role})`;
  };

  return (
    <>
    <a
      href="#main-content"
      style={{
        position: 'absolute',
        left: '-9999px',
        top: 'auto',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
        zIndex: 9999,
      }}
      onFocus={(e) => {
        e.currentTarget.style.position = 'fixed';
        e.currentTarget.style.left = '8px';
        e.currentTarget.style.top = '8px';
        e.currentTarget.style.width = 'auto';
        e.currentTarget.style.height = 'auto';
        e.currentTarget.style.overflow = 'visible';
        e.currentTarget.style.padding = '0.75rem 1.5rem';
        e.currentTarget.style.backgroundColor = '#D4A017';
        e.currentTarget.style.color = '#000';
        e.currentTarget.style.fontWeight = '700';
        e.currentTarget.style.borderRadius = '4px';
        e.currentTarget.style.textDecoration = 'none';
      }}
      onBlur={(e) => {
        e.currentTarget.style.position = 'absolute';
        e.currentTarget.style.left = '-9999px';
        e.currentTarget.style.width = '1px';
        e.currentTarget.style.height = '1px';
        e.currentTarget.style.overflow = 'hidden';
      }}
    >
      Skip to main content
    </a>
    <header
      className="app-header"
      role="banner"
      style={{
        backgroundColor: '#28527A',
        borderBottom: '1px solid #6CA6C1',
        padding: '1rem 0',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}
    >
      <style>{`
        /* Mobile-responsive header styles */
        @media (max-width: 768px) {
          .app-header {
            padding: 0.5rem 0 !important;
          }
          .header-container {
            padding: 0 1rem !important;
          }
          .header-logo-link {
            font-size: 1rem !important;
          }
          .header-company-name {
            font-size: 0.875rem !important;
          }
          .header-company-logo {
            height: 28px !important;
            width: 28px !important;
          }
          .header-welcome {
            display: none !important;
          }
          .header-nav-button {
            padding: 0.375rem 0.75rem !important;
            font-size: 0.75rem !important;
          }
        }
      `}</style>
      <div
        className="header-container"
        style={{
          width: '100%',
          padding: '0 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        {/* Logo and Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Link
              to={user ? getDashboardLink() : '/'}
              className="header-logo-link"
              style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#F7E9A0',
                textDecoration: 'none'
              }}
            >
              My ClinicCore
            </Link>

            {/* Company Logo and Name - Only show for clients */}
            {userType === 'client' && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginTop: '0.25rem'
              }}>
                {companySettings.companyLogo && (
                  <img
                    src={companySettings.companyLogo}
                    alt="Company Logo"
                    className="header-company-logo"
                    style={{
                      height: '40px',
                      width: '40px',
                      objectFit: 'contain',
                      borderRadius: '4px'
                    }}
                  />
                )}
                <span className="header-company-name" style={{
                  fontSize: '1.375rem',
                  color: '#D4A017',
                  fontWeight: '600'
                }}>
                  {companySettings.companyName}
                </span>
              </div>
            )}
          </div>
          
          {/* Session Status Indicator */}
          {showSessionStatus && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div 
                style={{
                  backgroundColor: sessionActive ? '#D4A017' : '#9ca3af',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%'
                }}
              ></div>
              <span 
                style={{ 
                  color: sessionActive ? '#D4A017' : '#9ca3af',
                  fontSize: '0.875rem'
                }}
              >
                {sessionActive ? 'Session Active' : 'No Active Session'}
              </span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav aria-label="Main navigation" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {user ? (
            /* Authenticated User Navigation */
            <>
              <span className="header-welcome" style={{
                color: '#d1d5db',
                fontSize: '0.875rem',
                marginRight: '1rem'
              }}>
                {getWelcomeMessage()}
              </span>

              {userType === 'provider' && (
                <Link
                  to="/provider-dashboard"
                  className="header-nav-button"
                  style={{
                    color: '#d1d5db',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.25rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#4b5563';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Dashboard
                </Link>
              )}

              {userType === 'client' && (
                <Link
                  to="/client-dashboard"
                  className="header-nav-button"
                  style={{
                    color: '#d1d5db',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.25rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#4b5563';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Dashboard
                </Link>
              )}

              {/* Read Aloud Toggle — client only */}
              {userType === 'client' && (
                <button
                  onClick={() => {
                    const newState = HopeVoiceService.toggle();
                    setReadAloudOn(newState);
                    // When turned on, read the main page content
                    if (newState) {
                      setTimeout(() => {
                        const main = document.getElementById('main-content') || document.querySelector('main');
                        if (main) {
                          HopeVoiceService.readPageSection(main as HTMLElement);
                        }
                      }, 1500); // Wait for "Hope is here" announcement to finish
                    }
                  }}
                  className="header-nav-button"
                  aria-label={readAloudOn ? 'Turn off read aloud' : 'Turn on read aloud'}
                  title={readAloudOn ? 'Read Aloud: ON' : 'Read Aloud: OFF'}
                  style={{
                    color: readAloudOn ? '#F7E9A0' : '#94a3b8',
                    fontSize: '0.875rem',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.25rem',
                    transition: 'all 0.2s',
                    backgroundColor: readAloudOn ? 'rgba(212, 160, 23, 0.15)' : 'transparent',
                    border: readAloudOn ? '1px solid rgba(212, 160, 23, 0.3)' : '1px solid transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = readAloudOn ? 'rgba(212, 160, 23, 0.25)' : '#4b5563';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = readAloudOn ? 'rgba(212, 160, 23, 0.15)' : 'transparent';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    {readAloudOn ? (
                      <>
                        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                      </>
                    ) : (
                      <line x1="23" y1="9" x2="17" y2="15" />
                    )}
                  </svg>
                  <span>{readAloudOn ? 'Reading' : 'Read Aloud'}</span>
                </button>
              )}

              {/* Font Size Controls — client only */}
              {userType === 'client' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                  <button
                    onClick={() => {
                      AccessibilityService.decreaseFontSize();
                      setFontSizeLabel(AccessibilityService.getFontSizeLabel());
                    }}
                    disabled={!AccessibilityService.canDecrease()}
                    className="header-nav-button"
                    aria-label="Decrease font size"
                    title="Decrease font size"
                    style={{
                      color: '#94a3b8',
                      fontSize: '0.8rem',
                      padding: '0.4rem 0.5rem',
                      borderRadius: '0.25rem 0 0 0.25rem',
                      border: '1px solid rgba(148, 163, 184, 0.3)',
                      backgroundColor: 'transparent',
                      cursor: AccessibilityService.canDecrease() ? 'pointer' : 'not-allowed',
                      opacity: AccessibilityService.canDecrease() ? 1 : 0.4,
                      fontWeight: 'bold',
                    }}
                  >
                    A-
                  </button>
                  <button
                    onClick={() => {
                      AccessibilityService.increaseFontSize();
                      setFontSizeLabel(AccessibilityService.getFontSizeLabel());
                    }}
                    disabled={!AccessibilityService.canIncrease()}
                    className="header-nav-button"
                    aria-label="Increase font size"
                    title="Increase font size"
                    style={{
                      color: '#94a3b8',
                      fontSize: '0.95rem',
                      padding: '0.4rem 0.5rem',
                      borderRadius: '0 0.25rem 0.25rem 0',
                      border: '1px solid rgba(148, 163, 184, 0.3)',
                      borderLeft: 'none',
                      backgroundColor: 'transparent',
                      cursor: AccessibilityService.canIncrease() ? 'pointer' : 'not-allowed',
                      opacity: AccessibilityService.canIncrease() ? 1 : 0.4,
                      fontWeight: 'bold',
                    }}
                  >
                    A+
                  </button>
                </div>
              )}

              {/* High Contrast Toggle — client only */}
              {userType === 'client' && (
                <button
                  onClick={() => {
                    const newState = AccessibilityService.toggleHighContrast();
                    setHighContrast(newState);
                  }}
                  className="header-nav-button"
                  aria-label={highContrast ? 'Turn off high contrast' : 'Turn on high contrast'}
                  title={highContrast ? 'High Contrast: ON' : 'High Contrast: OFF'}
                  style={{
                    color: highContrast ? '#FFD700' : '#94a3b8',
                    fontSize: '0.875rem',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.25rem',
                    transition: 'all 0.2s',
                    backgroundColor: highContrast ? 'rgba(255, 215, 0, 0.15)' : 'transparent',
                    border: highContrast ? '1px solid rgba(255, 215, 0, 0.3)' : '1px solid transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="5" />
                    <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                  </svg>
                  <span>{highContrast ? 'Contrast' : 'Contrast'}</span>
                </button>
              )}

              {/* My Account Dropdown */}
              <div style={{ position: 'relative' }} ref={dropdownRef}>
                <button
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className="header-nav-button"
                  aria-expanded={showProfileDropdown}
                  aria-haspopup="true"
                  aria-label="Account menu"
                  style={{
                    color: '#d1d5db',
                    fontSize: '0.875rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.25rem',
                    transition: 'background-color 0.2s',
                    backgroundColor: showProfileDropdown ? '#4b5563' : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  onMouseEnter={(e) => {
                    if (!showProfileDropdown) e.currentTarget.style.backgroundColor = '#4b5563';
                  }}
                  onMouseLeave={(e) => {
                    if (!showProfileDropdown) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span>My Account</span>
                  <span style={{
                    fontSize: '10px',
                    transform: showProfileDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    display: 'inline-block'
                  }}>▼</span>
                </button>

                {/* Dropdown Menu */}
                {showProfileDropdown && (
                  <div role="menu" aria-label="Account options" style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '8px',
                    backgroundColor: '#113F67',
                    border: '1px solid #6CA6C1',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    zIndex: 10000,
                    minWidth: '200px'
                  }}>
                    <button
                      role="menuitem"
                      onClick={() => {
                        if (onOpenProfileSettings) onOpenProfileSettings();
                        setShowProfileDropdown(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        borderBottom: '1px solid #6CA6C1',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#28527A'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      My Profile
                    </button>
                    {onOpenSettings && (
                    <button
                      role="menuitem"
                      onClick={() => {
                        onOpenSettings();
                        setShowProfileDropdown(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        borderBottom: '1px solid #6CA6C1',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#28527A'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      Settings
                    </button>
                    )}
                    <button
                      role="menuitem"
                      onClick={() => {
                        handleLogout();
                        setShowProfileDropdown(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        color: '#FFFFFF',
                        fontSize: '14px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        borderRadius: '0 0 8px 8px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#28527A'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Guest Navigation */
            <>
              <Link
                to="/client-login"
                className="btn btn-secondary header-nav-button"
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  textDecoration: 'none'
                }}
              >
                Client Login
              </Link>
              <Link
                to="/provider-login"
                className="btn btn-primary header-nav-button"
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  textDecoration: 'none'
                }}
              >
                Provider Login
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
    </>
  );
};

export default Header;
